
  import { Vue, Component, Prop } from 'vue-property-decorator'
  import { AppModule } from '@/store/module'

  const DEFAULT_COLUMN_SIZE = 8

  export interface NavigationMenuItem {
    name: string;
    routeName: string;
    active?: boolean;
    hover?: boolean;
  }

  export interface MenuCell {
    item: NavigationMenuItem;
    sourceIndex: number;
  }

  export type MenuColumn = MenuCell[]

  export interface PanelGeometry {
    triggerViewportLeft: number;
    panelTranslateX: number;
    panelWidth: number;
    contentInsetLeft: number;
  }

  export function normalizeViewportWidth(viewportWidth: number): number {
    return Number.isFinite(viewportWidth) && viewportWidth > 0
      ? viewportWidth
      : 0
  }

  export function normalizeTriggerViewportLeft(
    triggerViewportLeft: number,
    viewportWidth: number
  ): number {
    const safeViewportWidth = normalizeViewportWidth(viewportWidth)
    if (!Number.isFinite(triggerViewportLeft)) return 0

    return Math.min(Math.max(triggerViewportLeft, 0), safeViewportWidth)
  }

  export function calculatePanelGeometry(
    viewportWidth: number,
    triggerViewportLeft: number
  ): PanelGeometry {
    const panelWidth = normalizeViewportWidth(viewportWidth)
    const safeTriggerViewportLeft = normalizeTriggerViewportLeft(
      triggerViewportLeft,
      panelWidth
    )

    return {
      triggerViewportLeft: safeTriggerViewportLeft,
      panelTranslateX: -safeTriggerViewportLeft,
      panelWidth,
      contentInsetLeft: safeTriggerViewportLeft,
    }
  }

  export function buildColumns(
    list: ReadonlyArray<NavigationMenuItem>,
    size = DEFAULT_COLUMN_SIZE
  ): MenuColumn[] {
    const columnSize = Number.isFinite(size) && size > 0
      ? Math.floor(size)
      : DEFAULT_COLUMN_SIZE
    const cells = list.map((item, sourceIndex): MenuCell => ({
      item,
      sourceIndex,
    }))
    const columns: MenuColumn[] = []

    for (let start = 0; start < cells.length; start += columnSize) {
      columns.push(cells.slice(start, start + columnSize))
    }

    return columns
  }

  @Component({
    inheritAttrs: false,
  })
  export default class DropdownContentList extends Vue {

    @Prop({
      type: String,
      default: '',
    })
    readonly title!: string

    @Prop({
      type: Array,
      default: () => [],
    })
    readonly list!: NavigationMenuItem[]

    @Prop({
      type: Number,
      default: 0,
    })
    readonly triggerViewportLeft!: number

    hoveredIndex: number | null = null

    get classes(): string[] {
      return [
        'dropdown-content-list'
      ]
    }

    get viewportWidth(): number {
      return typeof window === 'undefined' ? 0 : window.innerWidth
    }

    get panelGeometry(): PanelGeometry {
      return calculatePanelGeometry(
        this.viewportWidth,
        this.triggerViewportLeft
      )
    }

    get columns(): MenuColumn[] {
      return buildColumns(this.list, DEFAULT_COLUMN_SIZE)
    }

    get style(): Record<string, string> {
      const style: Record<string, string> = {}
      for (const key in this.$attrs) {
        const values = String(this.$attrs[key]).split('|')
        if (values.length === 1 || !AppModule.isH5) {
          style[key] = this.px2rem(values[0])
        } else {
          style[key] = this.px2rem(values[1])
        }
      }

      const geometry = this.panelGeometry
      style['--panel-translate-x'] = `${geometry.panelTranslateX}px`
      style['--panel-width'] = `${geometry.panelWidth}px`
      style['--panel-content-inset-left'] = `${geometry.contentInsetLeft}px`

      return style
    }

    px2rem(value: string): string {
      if (value.indexOf('px') === -1) return value
      return (parseFloat(value) / Number(process.env.VUE_APP_ROOT_VALUE)) + 'rem'
    }

    onMouseEnter(sourceIndex: number): void {
      this.hoveredIndex = sourceIndex
    }

    onMouseLeave(sourceIndex: number): void {
      if (this.hoveredIndex === sourceIndex) {
        this.hoveredIndex = null
      }
    }
  }
